/**
 * A block's user-set height (spec §7.3, §8.6 item 8) `[M21]`.
 *
 * `height` sits on the tree node's data map beside `body`, `links`, `groups` and `facts`, not in a
 * type-specific body, so one grip serves every block type. It is written only once someone drags the
 * block's lower edge and deleted again on reset, so a block that has never been resized carries no
 * key, renders at its natural height and keeps its pre-M21 content hash. The projector excludes the
 * key from the hash anyway (§13.1): a resize produces no triples and must cost no projection work.
 */
import type { LoroDoc, LoroTreeNode } from "loro-crdt";

export const MIN_BLOCK_HEIGHT = 64;
export const MAX_BLOCK_HEIGHT = 4000;

export const clampHeight = (px: number): number => Math.round(Math.min(MAX_BLOCK_HEIGHT, Math.max(MIN_BLOCK_HEIGHT, px)));

/** The height in px, or `null` when the block has never been resized. */
export function readHeight(node: LoroTreeNode): number | null {
  const v = node.data.get("height");
  return typeof v === "number" && Number.isFinite(v) ? clampHeight(v) : null;
}

export function setHeight(doc: LoroDoc, node: LoroTreeNode, px: number): void {
  node.data.set("height", clampHeight(px));
  doc.commit();
}

/** Back to the natural height: the key goes, so the block is indistinguishable from an unresized one. */
export function clearHeight(doc: LoroDoc, node: LoroTreeNode): void {
  if (node.data.get("height") === undefined) return;
  node.data.delete("height");
  doc.commit();
}
